import {
  Sparkles,
  Zap,
  FileText,
  MessageSquare,
  Mail,
  Bug,
  GitPullRequest,
  ListChecks,
  Calendar,
  Bell,
  Search,
  Code,
  Terminal,
  Lightbulb,
  Pencil,
  BookOpen,
  Briefcase,
  Megaphone,
  Target,
  Rocket,
  Wand2,
  Brain,
  ClipboardList,
  PenTool,
  Bookmark,
  Flag,
  Compass,
  FolderOpen,
  type LucideIcon,
} from 'lucide-react'
import { useSyncExternalStore } from 'react'
import type { Skill } from '@renderer/chat/types'

// Lazy-loaded lucide namespace for resolving backend-provided icon names that
// aren't in the curated SKILL_ICONS set. Loaded as a dynamic import so the
// static wildcard `import * as LucideIcons` doesn't defeat tree-shaking on the
// named icon imports used throughout the renderer bundle. Exposed as a tiny
// external store so React consumers re-render when it resolves — render-time
// callers must go through the `useSkillIcon` / `useLucideIconByName` hooks
// below, or an early render pins the fallback icon until an unrelated update.
let _lucideNs: Record<string, unknown> | null = null
const lucideNsListeners = new Set<() => void>()
import('lucide-react')
  .then((mod) => {
    _lucideNs = mod as unknown as Record<string, unknown>
    for (const listener of lucideNsListeners) listener()
  })
  .catch((error) => {
    // Non-fatal: name lookups keep returning undefined and callers fall back
    // to the curated SKILL_ICONS set / default icon.
    console.error('Failed to load the lucide icon namespace:', error)
  })

function subscribeToLucideNs(listener: () => void): () => void {
  lucideNsListeners.add(listener)
  return () => {
    lucideNsListeners.delete(listener)
  }
}

const getLucideNsSnapshot = () => _lucideNs

// Converts a kebab-case Lucide icon name (e.g. "book-open") to the PascalCase
// export name (e.g. "BookOpen") and returns the component if it exists.
// Returns undefined if the lazy namespace hasn't resolved yet — from render,
// use `useLucideIconByName` so the icon upgrades when it does.
export function resolveLucideIconByName(name: string): LucideIcon | undefined {
  if (!_lucideNs) return undefined
  const pascalName = name
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join('')
  const candidate = _lucideNs[pascalName]
  // Lucide icons are forwardRef objects (typeof === 'object'), not plain functions
  return candidate != null ? (candidate as LucideIcon) : undefined
}

/** Render-safe {@link resolveLucideIconByName}: re-renders once the lazy namespace lands. */
export function useLucideIconByName(name: string): LucideIcon | undefined {
  useSyncExternalStore(subscribeToLucideNs, getLucideNsSnapshot)
  return resolveLucideIconByName(name)
}

// Curated icon set users can pick from. Keyed by a stable string id that is
// what gets persisted in skill.metadata.icon — never store the component.
export const SKILL_ICONS: Record<string, LucideIcon> = {
  sparkles: Sparkles,
  zap: Zap,
  'file-text': FileText,
  'message-square': MessageSquare,
  mail: Mail,
  bug: Bug,
  'git-pull-request': GitPullRequest,
  'list-checks': ListChecks,
  calendar: Calendar,
  bell: Bell,
  search: Search,
  code: Code,
  terminal: Terminal,
  lightbulb: Lightbulb,
  pencil: Pencil,
  'book-open': BookOpen,
  briefcase: Briefcase,
  megaphone: Megaphone,
  target: Target,
  rocket: Rocket,
  wand: Wand2,
  brain: Brain,
  'clipboard-list': ClipboardList,
  'pen-tool': PenTool,
  bookmark: Bookmark,
  flag: Flag,
  compass: Compass,
  'folder-open': FolderOpen,
}

export const SKILL_ICON_IDS = Object.keys(SKILL_ICONS)

// Color id -> CSS color value. The id is what gets persisted in
// skill.metadata.iconColor. Each hue is offered at a regular and a darker
// shade; unknown/legacy ids fall back to `default`.
//
// These are concrete CSS values (not tailwind classes) applied via inline
// `style` — that sidesteps Tailwind's content scan, which silently drops
// color classes that aren't referenced elsewhere in the build.
export const SKILL_ICON_COLORS: Array<{ id: string; value: string }> = [
  { id: 'default', value: 'oklch(from var(--foreground) l c h / 0.7)' },
  { id: 'blue', value: '#3b82f6' },
  { id: 'green', value: '#22c55e' },
  { id: 'red', value: '#ef4444' },
  { id: 'amber', value: '#f59e0b' },
  { id: 'violet', value: '#8b5cf6' },
  { id: 'pink', value: '#ec4899' },
  { id: 'teal', value: '#14b8a6' },
  { id: 'blue-dark', value: '#1d4ed8' },
  { id: 'green-dark', value: '#15803d' },
  { id: 'red-dark', value: '#b91c1c' },
  { id: 'amber-dark', value: '#b45309' },
  { id: 'violet-dark', value: '#6d28d9' },
  { id: 'pink-dark', value: '#be185d' },
  { id: 'teal-dark', value: '#0f766e' },
]

export const DEFAULT_SKILL_ICON_ID = 'sparkles'
export const DEFAULT_SKILL_ICON_COLOR_ID = 'default'

export interface SkillIconSelection {
  iconId: string
  colorId: string
}

export function colorValueFor(colorId: string): string {
  return SKILL_ICON_COLORS.find((c) => c.id === colorId)?.value ?? SKILL_ICON_COLORS[0].value
}

// Reads the icon selection out of a skill's free-form metadata bag,
// tolerating absent or malformed values.
export function readSkillIconSelection(
  skill: Pick<Skill, 'metadata'>,
  fallbackIconId: string = DEFAULT_SKILL_ICON_ID,
): SkillIconSelection {
  const metadata: Record<string, unknown> = skill.metadata ?? {}
  const iconRaw = metadata.icon
  const colorRaw = metadata.iconColor
  return {
    // Preserve the raw icon name even if it's not in the curated picker set;
    // the picker trigger resolves it dynamically via resolveLucideIconByName.
    iconId: typeof iconRaw === 'string' && iconRaw ? iconRaw : fallbackIconId,
    colorId:
      typeof colorRaw === 'string' && SKILL_ICON_COLORS.some((c) => c.id === colorRaw)
        ? colorRaw
        : DEFAULT_SKILL_ICON_COLOR_ID,
  }
}

// Resolves a skill's metadata into a renderable icon component + CSS color.
// Prefers the curated SKILL_ICONS set; if the backend supplies a Lucide icon
// name not in that set (e.g. "book", "send"), resolves it dynamically so the
// correct icon renders rather than the generic fallback. From render, use
// `useSkillIcon` so dynamically-resolved icons upgrade when the lazy
// namespace lands.
export function getSkillIcon(
  skill: Pick<Skill, 'metadata'>,
  fallbackIconId: string = DEFAULT_SKILL_ICON_ID,
): { Icon: LucideIcon; color: string; selection: SkillIconSelection } {
  const selection = readSkillIconSelection(skill, fallbackIconId)
  const rawIconName = typeof skill.metadata?.icon === 'string' ? skill.metadata.icon : null
  const Icon =
    (rawIconName ? (SKILL_ICONS[rawIconName] ?? resolveLucideIconByName(rawIconName)) : undefined) ??
    SKILL_ICONS[fallbackIconId] ??
    SKILL_ICONS[DEFAULT_SKILL_ICON_ID]
  return {
    Icon,
    color: colorValueFor(selection.colorId),
    selection,
  }
}

/** Render-safe {@link getSkillIcon}: re-renders once the lazy lucide namespace lands. */
export function useSkillIcon(
  skill: Pick<Skill, 'metadata'>,
  fallbackIconId: string = DEFAULT_SKILL_ICON_ID,
): { Icon: LucideIcon; color: string; selection: SkillIconSelection } {
  useSyncExternalStore(subscribeToLucideNs, getLucideNsSnapshot)
  return getSkillIcon(skill, fallbackIconId)
}

// Builds the metadata patch for a save, merging into the existing bag so
// unrelated metadata keys are preserved.
export function withSkillIconMetadata(
  metadata: Skill['metadata'],
  selection: SkillIconSelection,
): Record<string, unknown> {
  return { ...(metadata ?? {}), icon: selection.iconId, iconColor: selection.colorId }
}
